import { defineStore } from "pinia"
import { useCompanyStore, type CompanyContextToken } from "@/store/company-store"
import { useFinchConnect } from "@/modules/workforce-integrations/composables/finch-connect" // @ts-ignore
import {
  EMIProviderLinkStatus,
  EMIConnectionStatusEnum,
  EMIProviderLinkStatusEnum,
  EMISyncOperationEventTypeEnum,
  EMISystemTypeEnum,
  EMIVendorEnum,
  WorkforceIntegration
} from "@vitable/asclepius-api-client-ts"
import {
  useDisconnectWorkforceIntegration,
  useGetProviderLinkStatus,
  useListEmployeesNotFoundInHris,
  useListEmployeesWeCannotAddFromHris,
  useListSyncOperationEvents,
  useListWorkforceIntegrations
} from "@/modules/workforce-integrations/composables/workforce-integration"
import { DateTime } from "luxon"

export const HrisConnectionUIStateEnum = {
  Connected: "connected",
  SetupIncomplete: "setup_incomplete",
  ReauthRequired: "reauth_required",
  Paused: "paused"
} as const
export type HrisConnectionUIState = (typeof HrisConnectionUIStateEnum)[keyof typeof HrisConnectionUIStateEnum] | null

interface StoreState {
  hrisIntegrations: Array<WorkforceIntegration>
  hrisConnectionStatus: Nullable<EMIProviderLinkStatus>
  addedEventsLast30DaysCount: number
  removedEventsLast30DaysCount: number
  employeesWeCannotAddFromHrisCount: number
  employeesNotFoundInHrisCount: number
  isReconnecting: boolean
  hrisIntegrationsInitialized: boolean
  hrisIntegrationsLoadError: boolean
}

export const useWorkforceIntegrationsStore = defineStore("workforceIntegrationsStore", {
  persist: { storage: sessionStorage },
  state: (): StoreState => {
    return {
      hrisIntegrations: [],
      hrisConnectionStatus: null,
      addedEventsLast30DaysCount: 0,
      removedEventsLast30DaysCount: 0,
      employeesWeCannotAddFromHrisCount: 0,
      employeesNotFoundInHrisCount: 0,
      isReconnecting: false,
      hrisIntegrationsInitialized: false,
      hrisIntegrationsLoadError: false
    }
  },
  getters: {
    isHrisReauthRequired(): boolean {
      return this.hrisConnectionUIState === HrisConnectionUIStateEnum.ReauthRequired
    },
    hrisIntegration(): Nullable<WorkforceIntegration> {
      const availableIntegrations = this.hrisIntegrations.filter((i) => i.status !== EMIConnectionStatusEnum.Terminated)

      if (availableIntegrations.length > 0) {
        return availableIntegrations[0]
      }
      return null
    },
    totalNumEmployeeErrors(): number {
      return this.employeesWeCannotAddFromHrisCount + this.employeesNotFoundInHrisCount
    },
    hrisConnectionUIState(): HrisConnectionUIState {
      const integration = this.hrisIntegration
      if (!integration) return null

      const vitableStatus = integration.status
      const finchStatus = this.hrisConnectionStatus?.provider_link_status ?? null

      if (vitableStatus === EMIConnectionStatusEnum.Terminated) {
        console.error("[WorkforceIntegrations] Unexpected TERMINATED integration in hrisConnectionUIState.")
        return null
      }

      // Paused: deliberately paused by CS — show distinct paused experience
      if (vitableStatus === EMIConnectionStatusEnum.Paused) return HrisConnectionUIStateEnum.Paused

      // ReauthRequired: Vitable inactive OR Finch reauth
      if (vitableStatus === EMIConnectionStatusEnum.Inactive) return HrisConnectionUIStateEnum.ReauthRequired

      // If finchStatus hasn't loaded yet, return null (loading/unknown)
      if (finchStatus === null) return null

      if (finchStatus === EMIProviderLinkStatusEnum.Reauth) return HrisConnectionUIStateEnum.ReauthRequired

      // SetupIncomplete: Provider-side setup/permission errors
      if (
        finchStatus === EMIProviderLinkStatusEnum.ErrorNoAccountSetup ||
        finchStatus === EMIProviderLinkStatusEnum.ErrorPermissions
      ) {
        return HrisConnectionUIStateEnum.SetupIncomplete
      }

      // Connected: Finch reports connected, pending, or processing (optimistic)
      return HrisConnectionUIStateEnum.Connected
    },
    isHrisPaused(): boolean {
      return this.hrisConnectionUIState === HrisConnectionUIStateEnum.Paused
    },
    hasOperationalHrisIntegration(): boolean {
      return this.hrisIntegration != null && !this.isHrisPaused
    },
    isHrisConnectionIncomplete(): boolean {
      return this.hrisConnectionUIState === HrisConnectionUIStateEnum.SetupIncomplete
    },
    isHrisPermissionUpdateRequired(): boolean {
      return (
        this.hrisConnectionUIState === HrisConnectionUIStateEnum.Connected &&
        this.hrisConnectionStatus?.manual === false &&
        this.hrisConnectionStatus?.requires_permission_update_in === true
      )
    },
    hrisSetupIncompleteReason(): "no_account_setup" | "permissions" | null {
      if (this.hrisConnectionUIState !== HrisConnectionUIStateEnum.SetupIncomplete) return null
      if (this.hrisProviderConnectionStatus === EMIProviderLinkStatusEnum.ErrorNoAccountSetup) return "no_account_setup"
      if (this.hrisProviderConnectionStatus === EMIProviderLinkStatusEnum.ErrorPermissions) return "permissions"
      return null
    },
    isHrisConnectionWarning(): boolean {
      return this.hrisConnectionUIState != null && this.hrisConnectionUIState !== HrisConnectionUIStateEnum.Connected
    },
    hrisProviderConnectionStatus(): EMIProviderLinkStatusEnum | null {
      return this.hrisConnectionStatus?.provider_link_status ?? null
    },
    // Data freshness represents when data was last successfully synced FROM Finch TO Vitable.
    // We return the minimum of finchLastSync and vitableLastSync because:
    // - If Vitable is caught up: show Finch's date (they're equal or Finch is fresher)
    // - If Vitable is behind: show Vitable's date (that's what customers actually have)
    // TODO: Once we replace cron-based Finch→Vitable syncing with webhooks, this getter
    // can be simplified to just return finchLastSync directly (no cadence estimation needed).
    dataFreshnessDate(): DateTime | null {
      const finchLastSyncRaw = this.hrisConnectionStatus?.last_successful_sync
      const vitableLastSyncRaw = this.hrisIntegration?.last_sync_details?.last_sync_on
      if (!finchLastSyncRaw || !vitableLastSyncRaw) return null

      const finchLastSync = DateTime.fromISO(finchLastSyncRaw)
      const vitableLastSync = DateTime.fromISO(vitableLastSyncRaw)
      if (!finchLastSync.isValid || !vitableLastSync.isValid) return null

      if (finchLastSync <= vitableLastSync) {
        return finchLastSync
      }
      // Finch is ahead - return what we actually have in Vitable
      return vitableLastSync
    },
    // Finch sync cadence (webhooks deliver data to Vitable in near-real-time):
    //   Automated connections: ~24h (Finch syncs from provider every 24h, then fires data.updated webhook)
    //   Assisted connections: ~7d (Finch syncs every 7 days; no data.updated webhooks for assisted)
    estimatedNextFreshSync(): DateTime | null {
      const freshness = this.dataFreshnessDate
      if (!freshness) return null

      const fullPipelineCadence = this.hrisConnectionStatus?.manual ? { days: 7 } : { hours: 24 }
      return freshness.plus(fullPipelineCadence)
    }
  },
  actions: {
    async loadHrisIntegrations(contextToken?: CompanyContextToken) {
      const companyStore = useCompanyStore()
      const token = contextToken ?? companyStore.getCompanyContextToken()
      if (!token || !companyStore.isCompanyContextCurrent(token)) {
        console.error("Cannot load HRIS integrations: companyId is not set")
        return
      }
      this.hrisIntegrationsInitialized = false
      this.hrisIntegrationsLoadError = false
      const { integrations, listIntegrations } = useListWorkforceIntegrations()

      try {
        await listIntegrations({
          companyId: token.companyId,
          systemType: EMISystemTypeEnum.Hris
        })
      } catch (error: unknown) {
        if (companyStore.isCompanyContextCurrent(token)) {
          this.hrisIntegrationsLoadError = true
        }
        throw error
      }

      if (companyStore.isCompanyContextCurrent(token)) {
        this.hrisIntegrations = integrations.value
        this.hrisIntegrationsInitialized = true
      }
    },

    async loadRecentEventCounts(contextToken?: CompanyContextToken) {
      const companyStore = useCompanyStore()
      const token = contextToken ?? companyStore.getCompanyContextToken()
      if (!token || !companyStore.isCompanyContextCurrent(token)) {
        console.error("Cannot load recent event counts: companyId is not set")
        return
      }
      const { listSyncEvents, syncEventsPaginated } = useListSyncOperationEvents()

      const createdAfter30Days = DateTime.now().minus({ days: 31 }).toISODate()

      await listSyncEvents({
        companyId: token.companyId,
        createdAfter: createdAfter30Days,
        eventType: EMISyncOperationEventTypeEnum.Added,
        errorOccurredIn: false
      })

      if (!companyStore.isCompanyContextCurrent(token)) return
      this.addedEventsLast30DaysCount = syncEventsPaginated.totalCount

      await listSyncEvents({
        companyId: token.companyId,
        createdAfter: createdAfter30Days,
        eventType: EMISyncOperationEventTypeEnum.Removed,
        errorOccurredIn: false
      })

      if (companyStore.isCompanyContextCurrent(token)) {
        this.removedEventsLast30DaysCount = syncEventsPaginated.totalCount
      }
    },

    async loadEmployeesWeCannotAddFromHrisCount(contextToken?: CompanyContextToken) {
      const companyStore = useCompanyStore()
      const token = contextToken ?? companyStore.getCompanyContextToken()
      if (!token || !companyStore.isCompanyContextCurrent(token)) {
        console.error("Cannot load employees we cannot add from HRIS: companyId is not set")
        return
      }
      const { listEmployeesWeCannotAddFromHris } = useListEmployeesWeCannotAddFromHris({
        companyId: token.companyId
      })
      const count = (await listEmployeesWeCannotAddFromHris()).count
      if (companyStore.isCompanyContextCurrent(token)) this.employeesWeCannotAddFromHrisCount = count
    },

    async loadEmployeesNotFoundInHrisCount(contextToken?: CompanyContextToken) {
      const companyStore = useCompanyStore()
      const token = contextToken ?? companyStore.getCompanyContextToken()
      if (!token || !companyStore.isCompanyContextCurrent(token)) {
        console.error("Cannot load employees not found in HRIS: companyId is not set")
        return
      }
      const { listEmployeesNotFoundInHris } = useListEmployeesNotFoundInHris({ companyId: token.companyId })
      const count = (await listEmployeesNotFoundInHris()).count
      if (companyStore.isCompanyContextCurrent(token)) this.employeesNotFoundInHrisCount = count
    },

    async loadHrisConnectionStatus(contextToken?: CompanyContextToken) {
      const companyStore = useCompanyStore()
      const token = contextToken ?? companyStore.getCompanyContextToken()
      const integration = this.hrisIntegration
      if (!token || !companyStore.isCompanyContextCurrent(token) || !integration) return
      this.hrisConnectionStatus = null
      if (integration.vendor !== EMIVendorEnum.Finch) return

      const { getProviderLinkStatus, providerLinkStatus } = useGetProviderLinkStatus()
      await getProviderLinkStatus({ integrationId: integration.id })
      if (companyStore.isCompanyContextCurrent(token)) this.hrisConnectionStatus = providerLinkStatus.value
    },

    async disconnectHrisIntegration() {
      if (!this.hrisIntegration) throw new Error("No HRIS integration found to disconnect.")
      const { disconnectIntegration, disconnectedIntegration } = useDisconnectWorkforceIntegration()
      await disconnectIntegration({ integrationId: this.hrisIntegration.id })
      this.hrisIntegrations = this.hrisIntegrations.filter((i) => i.id !== disconnectedIntegration.value?.id)
      this.hrisConnectionStatus = null
    },

    async reconnectHrisIntegration() {
      if (!this.hrisIntegration) throw new Error("No HRIS integration found to reconnect.")
      const companyStore = useCompanyStore()
      if (!companyStore.companyId) throw new Error("Cannot reconnect: companyId is not set.")
      this.isReconnecting = true
      try {
        const { startReconnection } = useFinchConnect(companyStore.companyId)
        await startReconnection(this.hrisIntegration)
        // Browser navigates away on success (location.href redirect)
      } catch (e) {
        this.isReconnecting = false
        throw e
      }
    }
  }
})
